import angular from 'angular'

interface ApplicationProtectShape {
  readonly completelyUninstrumented: boolean
}

export type ApplicationProtectData = null | ApplicationProtectShape

export interface ShowProtectScoreFnInput {
  readonly isRASPEnabled: boolean
  readonly isProtectFeatureEnabled: boolean
  readonly model: {
    readonly app: {
      readonly isProtectLanguageSupported: boolean
      readonly protect: ApplicationProtectData
    }
  }
}

type ShowProtectScoreFn = (data: ShowProtectScoreFnInput) => boolean

export const showProtectScore: ShowProtectScoreFn = (data) =>
  // isRASPEnabled is true when the following are both true:
  // - GET /users/access returns true for rasp_access
  // - the "rasp" feature flag is on
  data.isRASPEnabled &&
  // isProtectFeatureEnabled is true when at least one of this application's
  // servers has "defend" set to true
  data.isProtectFeatureEnabled &&
  // isProtectLanguageSupported is true if the "Protect" agent feature
  // is enabled for this application's langauge
  data.model.app.isProtectLanguageSupported &&
  // Do not show if application is completely uninstrumented for protect
  !isApplicationCompletelyUninstrumented(data.model.app.protect)

export const isApplicationCompletelyUninstrumented = (
  data: ApplicationProtectData
) => (!data ? false : data.completelyUninstrumented)

export function ApplicationOverviewCtrl(
  $scope,
  $q,
  $rootScope,
  $routeParams,
  $translate,
  logger,
  permissions,
  ApplicationService,
  ErrorService,
  LoadFilterRouteService,
  UserService,
  appLibrariesService,
  ApplicationFactory,
  ServerFactory,
  SERVER_ENVIRONMENT,
  SCORE_TYPE,
  RemediationPolicyService,
  onboardingTutorialConstants,
  FeaturesService,
  AGENT_FEATURE
) {
  'ngInject'

  const translGlobal = {
    detail_serverRestartRequired: $translate.instant(
      'detail_serverRestartRequired'
    )
  }

  let _deregisterAppLicensedListener
  let _deregisterEnvUpdatedListener
  let _deregisterAppUpdateListener

  // For matching environment constant values to the abbreviated keys used in this controller
  // Should eliminate this at some point
  const _envKeyLookup = {}
  _envKeyLookup[SERVER_ENVIRONMENT.DEVELOPMENT.value] = 'dev'
  _envKeyLookup[SERVER_ENVIRONMENT.QA.value] = 'qa'
  _envKeyLookup[SERVER_ENVIRONMENT.PRODUCTION.value] = 'prod'

  angular.extend($scope, {
    // Environment Configs
    environment: {
      dev: {},
      qa: {},
      prod: {}
    },

    serverRestart: {
      dev: {
        assessment: false,
        defense: false
      },
      qa: {
        assessment: false,
        defense: false
      },
      prod: {
        assessment: false,
        defense: false
      }
    },

    assess: {
      assessment: false
    },

    loadLibraryPromise: false,
    loadServerPromise: false,

    isProtectFeatureEnabled: false,
    isAssessFeatureEnabled: false,
    isRASPEnabled: false,
    showProtectScore: () => showProtectScore($scope),

    model: {
      app: {
        isProtectLanguageSupported: false,
        isAssessLanguageSupported: false,
        defenseScore: {
          grade: undefined
        }
      },
      appId: $routeParams.appId,
      orgId: $routeParams.orgUuid,
      // type: VULNERABILITY_REPORT_TYPE.NEW,
      range: 'week'
    },

    tutorial: UserService.createOnboardingTutorialItem(
      onboardingTutorialConstants.TUTORIAL_TYPE.APP_OVERVIEW
    ),

    scoreType: SCORE_TYPE,
    linkToVulnerableLibs: '',
    linkToAppServers: '',
    showTurnOnAssessModal: false
  })

  angular.extend($scope, {
    turnOnAssess,
    disableProtection,
    disableAssessment,
    isProxy,
    closeTurnOnAssessModal
  })

  function init() {
    setLinkToVulnerableLibs()
    setLinkToAppServers()

    if (!_deregisterAppLicensedListener) {
      _deregisterAppLicensedListener = $rootScope.$on(
        'app.licensed',
        getApplication
      )
    }

    if (!_deregisterEnvUpdatedListener) {
      _deregisterEnvUpdatedListener = $rootScope.$on(
        'environment:updated',
        (_, args) => {
          switch (args.environment) {
            case SERVER_ENVIRONMENT.DEVELOPMENT.value:
              getServers().then(() => {
                getDevSettings()
              })
              break

            case SERVER_ENVIRONMENT.QA.value:
              getServers().then(() => {
                getQASettings()
              })
              break

            case SERVER_ENVIRONMENT.PRODUCTION.value:
              getServers().then(() => {
                getProdSettings()
              })
              break

            default:
              break
          }
        }
      )
    }

    $scope.$on('$destroy', _destroy)

    getApplication()

    $scope.loadServerPromise = false
    getServers().then(() => {
      $scope.loadServerPromise = true
      getAllEnvironmentSettings()
    })

    $scope.loadLibraryPromise = false
    getLibStats().then(() => {
      $scope.loadLibraryPromise = true
    })

    if (!_deregisterAppUpdateListener) {
      _deregisterAppUpdateListener = $scope.$on('app:update', () => {
        getApplication()
      })
    }

    $scope.$watch('servers', (newVal) => {
      if (Array.isArray(newVal)) {
        $scope.isAssessFeatureEnabled = newVal.some((server) => {
          return server.assess
        })

        $scope.isProtectFeatureEnabled = newVal.some((server) => {
          return server.defend
        })
      }
    })
  }

  function _destroy() {
    if (angular.isFunction(_deregisterAppLicensedListener)) {
      _deregisterAppLicensedListener()
    }
    if (angular.isFunction(_deregisterEnvUpdatedListener)) {
      _deregisterEnvUpdatedListener()
    }
    if (angular.isFunction(_deregisterAppUpdateListener)) {
      _deregisterAppUpdateListener()
    }
  }

  function setLinkToVulnerableLibs() {
    $scope.linkToVulnerableLibs =
      LoadFilterRouteService.buildURLToApplicationLibraries(
        $scope.model.appId,
        true
      )
  }

  function setLinkToAppServers() {
    $scope.linkToAppServers =
      LoadFilterRouteService.buildURLToServersFilteredByApp($scope.model.appId)
  }

  function disableAssessment(environment, servers) {
    const transl = {
      detail_applicationOverview_assessDisabledSuccessfully: $translate.instant(
        'detail_applicationOverview_assessDisabledSuccessfully'
      )
    }

    const promises: any[] = []

    angular.forEach(servers, (server) => {
      if (server.assess) {
        promises.push(ServerFactory.toggleAssess(server.server_id))
      }
    })

    $q.all(promises).then(
      () => {
        logger.logSuccess(
          transl.detail_applicationOverview_assessDisabledSuccessfully +
            '-' +
            translGlobal.detail_serverRestartRequired
        )
        switch (environment) {
          case SERVER_ENVIRONMENT.DEVELOPMENT.value:
            getServers().then(() => {
              getDevSettings()
            })
            break

          case SERVER_ENVIRONMENT.QA.value:
            getServers().then(() => {
              getQASettings()
            })
            break

          case SERVER_ENVIRONMENT.PRODUCTION.value:
            getServers().then(() => {
              getProdSettings()
            })
            break

          default:
            break
        }
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function disableProtection(environment, servers) {
    const transl = {
      detail_applicationOverview_protectDisabledSuccessfully:
        $translate.instant(
          'detail_applicationOverview_protectDisabledSuccessfully'
        )
    }

    const promises: any[] = []

    angular.forEach(servers, (server) => {
      if (server.defend) {
        promises.push(ServerFactory.toggleDefend(server.server_id))
      }
    })

    $q.all(promises).then(
      () => {
        logger.logSuccess(
          transl.detail_applicationOverview_protectDisabledSuccessfully +
            '-' +
            translGlobal.detail_serverRestartRequired
        )
        switch (environment) {
          case SERVER_ENVIRONMENT.DEVELOPMENT.value:
            getServers().then(() => {
              getDevSettings()
            })
            break

          case SERVER_ENVIRONMENT.QA.value:
            getServers().then(() => {
              getQASettings()
            })
            break

          case SERVER_ENVIRONMENT.PRODUCTION.value:
            getServers().then(() => {
              getProdSettings()
            })
            break

          default:
            break
        }
      },
      (response) => {
        ErrorService.processError(response)
      }
    )
  }

  function turnOnAssess() {
    if ($scope.servers.length > 0) {
      LoadFilterRouteService.goToServersFilteredByApp($routeParams.appId)
    } else {
      $scope.showTurnOnAssessModal = true
    }
  }

  function closeTurnOnAssessModal() {
    $scope.showTurnOnAssessModal = false
  }

  function getApplication() {
    ApplicationFactory.getApplication(
      $scope.model.appId,
      'scores,license,trace_breakdown,coverage,app_instrumentation_protect,routing_frameworks'
    ).then((getApplicationResponse) => {
      const app = getApplicationResponse.data.application
      $scope.model.app = app
      $scope.model.app.isDefaultLibraryScoringType = false
      $scope.model.app.isDefaultOverallScoringType = false
      $scope.isRASPEnabled = permissions.isRASPEnabled()

      if ($scope.isRASPEnabled) {
        ApplicationFactory.getDefenseScore(app.app_id).then(
          (getDefenseScoreResponse) => {
            $scope.model.app.defenseScore = getDefenseScoreResponse.data.scores
            $scope.model.app.isDefaultLibraryScoringType =
              getDefenseScoreResponse.data.scores.library_scoring_type ===
              'DEFAULT'

            $scope.model.app.isDefaultOverallScoringType =
              getDefenseScoreResponse.data.scores.overall_scoring_type ===
              'DEFAULT'
          }
        )
      } else {
        $scope.model.app.isDefaultLibraryScoringType =
          app.scores.library_scoring_type === 'DEFAULT'

        $scope.model.app.isDefaultOverallScoringType =
          app.scores.overall_scoring_type === 'DEFAULT'
      }

      FeaturesService.isAgentFeatureEnabled(AGENT_FEATURE.ASSESS, app.language)
        .then((assessFeatureEnabledResponse) => {
          $scope.model.app.isAssessLanguageSupported =
            assessFeatureEnabledResponse
        })
        .catch(ErrorService.processError)

      FeaturesService.isAgentFeatureEnabled(
        AGENT_FEATURE.PROTECTION,
        app.language
      )
        .then((protectFeatureEnabledResponse) => {
          $scope.model.app.isProtectLanguageSupported =
            protectFeatureEnabledResponse
        })
        .catch(ErrorService.processError)

      const roles = getApplicationResponse.data.application.roles
      permissions.updateAppPermissions($scope.model.appId, roles)
    })
  }

  function getAllEnvironmentSettings() {
    getDevSettings().then(() => {
      return getViolationsByEnv(SERVER_ENVIRONMENT.DEVELOPMENT.value)
    })
    getQASettings().then(() => {
      return getViolationsByEnv(SERVER_ENVIRONMENT.QA.value)
    })
    getProdSettings().then(() => {
      return getViolationsByEnv(SERVER_ENVIRONMENT.PRODUCTION.value)
    })
  }

  function getEnvironmentSettings(environment) {
    return ApplicationFactory.getServerProtectionSettings(
      $scope.model.appId,
      environment,
      true,
      true
    ).then((response) => {
      const quantity: number = getServersQuantityWithInstrumentationConflict(
        response.data,
        $scope.servers
      )
      return ApplicationService.buildEnvironmentSettings(
        response.data,
        quantity
      )
    }, ErrorService.processError)
  }

  function getServersQuantityWithInstrumentationConflict(data, servers) {
    return servers.filter(
      (server) =>
        server.environment === data.environment &&
        server.has_instrumentation_conflict
    ).length
  }

  function getDevSettings() {
    return getEnvironmentSettings(SERVER_ENVIRONMENT.DEVELOPMENT.value).then(
      (settings) => {
        $scope.environment.dev = settings
        $scope.environment.dev.serverRestart = $scope.serverRestart.dev
      }
    )
  }

  function getQASettings() {
    return getEnvironmentSettings(SERVER_ENVIRONMENT.QA.value).then(
      (settings) => {
        $scope.environment.qa = settings
        $scope.environment.qa.serverRestart = $scope.serverRestart.qa
      }
    )
  }

  function getProdSettings() {
    return getEnvironmentSettings(SERVER_ENVIRONMENT.PRODUCTION.value).then(
      (settings) => {
        $scope.environment.prod = settings
        $scope.environment.prod.serverRestart = $scope.serverRestart.prod
      }
    )
  }

  function getViolationsByEnv(env) {
    return RemediationPolicyService.getAppTracesPolicyViolations(
      $scope.model.appId,
      env
    ).then((traces) => {
      const key = _envKeyLookup[env]
      $scope.environment[key].violations = traces
    })
  }

  function getServers() {
    return ApplicationFactory.getAppServers($scope.model.appId)
      .then(_handleServersResponse)
      .catch(ErrorService.processError)
  }

  function getLibStats() {
    return appLibrariesService
      .getLibrariesStats($scope.model.appId)
      .then((stats) => {
        $scope.model.libs = {
          total: stats.total,
          vulnerables: stats.vulnerables
        }
      })
  }

  function _handleServersResponse(response) {
    $scope.servers = response.data.servers

    const serverRestart = {
      dev: {
        assessment: false,
        defense: false
      },
      qa: {
        assessment: false,
        defense: false
      },
      prod: {
        assessment: false,
        defense: false
      }
    }

    angular.forEach($scope.servers, (server) => {
      if (server.assessPending) {
        if (server.environment === SERVER_ENVIRONMENT.DEVELOPMENT.value) {
          serverRestart.dev.assessment = true
        } else if (server.environment === SERVER_ENVIRONMENT.QA.value) {
          serverRestart.qa.assessment = true
        } else if (server.environment === SERVER_ENVIRONMENT.PRODUCTION.value) {
          serverRestart.prod.assessment = true
        }
      }
      if (server.defendPending) {
        if (server.environment === SERVER_ENVIRONMENT.DEVELOPMENT.value) {
          serverRestart.dev.defense = true
        } else if (server.environment === SERVER_ENVIRONMENT.QA.value) {
          serverRestart.qa.defense = true
        } else if (server.environment === SERVER_ENVIRONMENT.PRODUCTION.value) {
          serverRestart.prod.defense = true
        }
      }
    })

    $scope.serverRestart = serverRestart
  }

  function isProxy(application) {
    return application && $scope.model.app.language === 'Proxy'
  }

  init()
}
